import React, { useState } from 'react';
import axios from 'axios';
import { Martini, Send, Loader2, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isCocktailJob = (job) =>
  (job?.job_type || '').toLowerCase().includes('cocktail') ||
  (job?.cocktails_selected || []).length > 0;

export const JobCocktailStatus = ({ job, token, onChanged }) => {
  const [sending, setSending] = useState(false);

  if (!isCocktailJob(job)) return null;

  const chosen = (job.cocktails_selected || []).filter(Boolean);
  const hasChoices = chosen.length > 0;

  const sendReminder = async () => {
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/jobs/${job.id}/send-cocktail-reminder`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        res.data.email_sent
          ? res.data.message
          : 'Reminder prepared, but email is not connected yet — link: ' + res.data.link
      );
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send the reminder');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`cocktails-${job.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <Martini className="w-3.5 h-3.5 text-gold-deep" />
            {hasChoices ? 'Cocktails chosen' : 'Not chosen yet'}
            {hasChoices && <Check className="w-3.5 h-3.5 text-green-600" />}
          </p>

          {hasChoices ? (
            <div className="flex flex-wrap gap-1 mt-2" data-testid={`cocktail-list-${job.id}`}>
              {chosen.map((cocktail, i) => (
                <Badge key={i} variant="outline" className="text-xs bg-white">{cocktail}</Badge>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-500 mt-1">
              The customer has not picked their cocktails from the website yet.
            </p>
          )}

          {job.cocktails_offmenu && (
            <p className="text-xs text-slate-600 mt-2"><strong>Off-menu request:</strong> {job.cocktails_offmenu}</p>
          )}
          {job.cocktails_notes && (
            <p className="text-xs text-slate-600 mt-1"><strong>Notes:</strong> {job.cocktails_notes}</p>
          )}
          {job.cocktails_allowed && (
            <p className="text-xs text-slate-400 mt-1">Allowed: {job.cocktails_allowed}</p>
          )}
        </div>

        <Button
          size="sm" variant="outline"
          className="flex-shrink-0 text-gold-deep"
          disabled={sending}
          onClick={sendReminder}
          data-testid={`send-cocktail-reminder-${job.id}`}
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
          Send cocktail reminder
        </Button>
      </div>
    </div>
  );
};

export default JobCocktailStatus;
